import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  LayoutDashboard, 
  PlayCircle, 
  FolderOpen, 
  Monitor, 
  ChevronDown, 
  ChevronRight,
  PlusSquare,
  Server,
  Cpu,
  Video
} from 'lucide-react';
import { Tree, Spin, Badge } from 'antd';
import { EnvironmentOutlined, DesktopOutlined, SendOutlined, UserOutlined, VideoCameraOutlined } from '@ant-design/icons';
import axios from 'axios';

const Sidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  const [treeData, setTreeData] = useState([]);
  const [unregisteredCount, setUnregisteredCount] = useState(0);
  const [loading, setLoading] = useState(true);
  
  const [isBoxesOpen, setIsBoxesOpen] = useState(false);
  const [expandedKeys, setExpandedKeys] = useState([]);

  // LOGIKA ZA DOZVOLE
  const userStr = localStorage.getItem('user');
  const user = userStr ? JSON.parse(userStr) : null;
  const isSuperuser = user?.is_superuser || false;
  const perms = user?.perms || {};

  const canSee = (page) => isSuperuser || perms[page] === true;

  const fetchData = async () => {
    try {
      const resList = await axios.get('/api/box/list/');
      
      const filteredBoxes = resList.data.filter(box => box.id !== 294);

      const locations = {};
      filteredBoxes.forEach(box => {
        const locName = box.location || 'Unknown';
        if (!locations[locName]) locations[locName] = [];
        locations[locName].push(box);
      });

      const formattedTree = Object.keys(locations).map((loc, index) => ({
        title: loc.toUpperCase(),
        key: `loc-${index}`,
        selectable: false,
        icon: <EnvironmentOutlined style={{ color: '#60a5fa', fontSize: '14px' }} />,
        children: locations[loc].map(box => ({
          title: box.box_name || box.box_id,
          key: box.id.toString(),
          icon: <DesktopOutlined style={{ color: box.status === 'online' ? '#34d399' : '#ef4444', fontSize: '14px' }} />,
          isLeaf: true
        }))
      }));
      setTreeData(formattedTree);

      const resUnreg = await axios.get('/api/box/registration-data/');
      setUnregisteredCount(resUnreg.data.unregistered.length);
      
      setLoading(false);
    } catch (error) {
      console.error('Error fetching sidebar data:', error);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, []);

  const onSelect = (selectedKeys, info) => {
    if (info.node.isLeaf) {
      navigate(`/screendetails/${info.node.key}`);
    }
  };

  const isInsideBox = location.pathname.startsWith('/screendetails/');
  const isRegistrationActive = location.pathname === '/registration';
  const isMappingsActive = location.pathname === '/mappings';
  const isRegisterMachineActive = location.pathname === '/register-machine';
  const isVideoEditorActive = location.pathname === '/video-editor';
  const isVideoEditor2Active = location.pathname === '/video-editor-2'; // DODATO
  const isTelegramUsersActive = location.pathname === '/telegram-users';
  const isUsersActive = location.pathname === '/users';
  const currentBoxId = location.pathname.split('/').pop();

  return (
    <div style={{
      width: '240px', height: 'calc(100vh - 105px)', background: '#1e293b', color: '#94a3b8',
      position: 'fixed', left: 0, top: '65px', borderRight: 'none', display: 'flex',
      flexDirection: 'column', zIndex: 1000, padding: '15px 0'
    }}>
      <nav style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>
        <ul style={{ listStyle: 'none', padding: '0', margin: 0 }}>
          
          {/* DASHBOARD */}
          {canSee('dashboard') && (
            <li onClick={() => navigate('/')} style={{
              marginBottom: '4px', display: 'flex', alignItems: 'center', padding: '12px 20px',
              cursor: 'pointer', fontSize: '0.9rem', color: location.pathname === '/' ? 'white' : '#94a3b8',
              background: location.pathname === '/' ? 'rgba(59, 130, 246, 0.1)' : 'transparent', transition: 'all 0.2s',
              position: 'relative'
            }}>
              {location.pathname === '/' && <div className="active-line-left" />}
              <span style={{ color: location.pathname === '/' ? '#60a5fa' : '#64748b', marginRight: '12px', display: 'flex' }}>
                <LayoutDashboard size={18} />
              </span>
              Dashboard
            </li>
          )}

          {/* MEDIA LIBRARY */}
          {canSee('media') && (
            <li onClick={() => navigate('/media')} style={{
              marginBottom: '4px', display: 'flex', alignItems: 'center', padding: '12px 20px',
              cursor: 'pointer', fontSize: '0.9rem', color: location.pathname === '/media' ? 'white' : '#94a3b8',
              background: location.pathname === '/media' ? 'rgba(59, 130, 246, 0.1)' : 'transparent', transition: 'all 0.2s',
              position: 'relative'
            }}>
              {location.pathname === '/media' && <div className="active-line-left" />}
              <span style={{ color: location.pathname === '/media' ? '#60a5fa' : '#64748b', marginRight: '12px', display: 'flex' }}>
                <FolderOpen size={18} />
              </span>
              Media Library
            </li>
          )}

          {/* PLAYLISTS */}
          {canSee('playlists') && (
            <li onClick={() => navigate('/playlists')} style={{
              marginBottom: '4px', display: 'flex', alignItems: 'center', padding: '12px 20px',
              cursor: 'pointer', fontSize: '0.9rem', color: location.pathname === '/playlists' ? 'white' : '#94a3b8',
              background: location.pathname === '/playlists' ? 'rgba(59, 130, 246, 0.1)' : 'transparent', transition: 'all 0.2s',
              position: 'relative'
            }}>
              {location.pathname === '/playlists' && <div className="active-line-left" />}
              <span style={{ color: location.pathname === '/playlists' ? '#60a5fa' : '#64748b', marginRight: '12px', display: 'flex' }}>
                <PlayCircle size={18} />
              </span>
              Playlists
            </li>
          )}

          {/* VIDEO EDITOR */}
          {canSee('video_editor') && (
            <li onClick={() => navigate('/video-editor')} style={{
              marginBottom: '4px', display: 'flex', alignItems: 'center', padding: '12px 20px',
              cursor: 'pointer', fontSize: '0.9rem', 
              color: isVideoEditorActive ? 'white' : '#94a3b8',
              background: isVideoEditorActive ? 'rgba(59, 130, 246, 0.1)' : 'transparent',
              transition: 'all 0.2s', position: 'relative'
            }}>
              {isVideoEditorActive && <div className="active-line-left" />}
              <span style={{ color: isVideoEditorActive ? '#60a5fa' : '#64748b', marginRight: '12px', display: 'flex' }}>
                <Video size={18} />
              </span>
              Video Editor
            </li>
          )}

          {/* VIDEO EDITOR 2 - NOVO DUGME */}
          {canSee('video_editor') && (
            <li onClick={() => navigate('/video-editor-2')} style={{
              marginBottom: '4px', display: 'flex', alignItems: 'center', padding: '12px 20px',
              cursor: 'pointer', fontSize: '0.9rem', 
              color: isVideoEditor2Active ? 'white' : '#94a3b8',
              background: isVideoEditor2Active ? 'rgba(59, 130, 246, 0.1)' : 'transparent',
              transition: 'all 0.2s', position: 'relative'
            }}>
              {isVideoEditor2Active && <div className="active-line-left" />}
              <span style={{ color: isVideoEditor2Active ? '#60a5fa' : '#64748b', marginRight: '12px', display: 'flex' }}>
                <VideoCameraOutlined style={{ fontSize: '18px' }} />
              </span>
              Video Editor 2
            </li>
          )}

          {/* REGISTER MACHINE */}
          {canSee('machines') && (
            <li onClick={() => navigate('/register-machine')} style={{
              marginBottom: '4px', display: 'flex', alignItems: 'center', padding: '12px 20px',
              cursor: 'pointer', fontSize: '0.9rem', 
              color: isRegisterMachineActive ? 'white' : '#94a3b8',
              background: isRegisterMachineActive ? 'rgba(59, 130, 246, 0.1)' : 'transparent',
              transition: 'all 0.2s', position: 'relative'
            }}>
              {isRegisterMachineActive && <div className="active-line-left" />}
              <span style={{ color: isRegisterMachineActive ? '#60a5fa' : '#64748b', marginRight: '12px', display: 'flex' }}>
                <Cpu size={18} />
              </span>
              Register Machine
            </li>
          )}

          {/* MACHINE MAPPINGS */}
          {canSee('mappings') && (
            <li onClick={() => navigate('/mappings')} style={{
              marginBottom: '4px', display: 'flex', alignItems: 'center', padding: '12px 20px',
              cursor: 'pointer', fontSize: '0.9rem', 
              color: isMappingsActive ? 'white' : '#94a3b8',
              background: isMappingsActive ? 'rgba(59, 130, 246, 0.1)' : 'transparent',
              transition: 'all 0.2s', position: 'relative'
            }}>
              {isMappingsActive && <div className="active-line-left" />}
              <span style={{ color: isMappingsActive ? '#60a5fa' : '#64748b', marginRight: '12px', display: 'flex' }}>
                <Server size={18} />
              </span>
              Machine Mappings
            </li>
          )}

          {/* REGISTER BOX */}
          {isSuperuser && (
            <li onClick={() => navigate('/registration')} style={{
              marginBottom: '4px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '12px 20px', cursor: 'pointer', fontSize: '0.9rem', 
              color: isRegistrationActive ? 'white' : '#94a3b8',
              background: isRegistrationActive ? 'rgba(59, 130, 246, 0.1)' : 'transparent',
              transition: 'all 0.2s', position: 'relative'
            }}>
              {isRegistrationActive && <div className="active-line-left" />}
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <span style={{ 
                  color: unregisteredCount > 0 ? '#fbbf24' : '#64748b', 
                  marginRight: '12px', 
                  display: 'flex',
                  filter: unregisteredCount > 0 ? 'drop-shadow(0 0 5px #fbbf24)' : 'none' 
                }}>
                  <PlusSquare size={18} />
                </span>
                Register Box
              </div>
              {unregisteredCount > 0 && (
                <Badge 
                  count={unregisteredCount} 
                  size="small" 
                  style={{ backgroundColor: '#ef4444' }} 
                />
              )}
            </li>
          )}

          {/* TELEGRAM USERS */}
          {canSee('telegram') && (
            <li onClick={() => navigate('/telegram-users')} style={{
              marginBottom: '4px', display: 'flex', alignItems: 'center', padding: '12px 20px',
              cursor: 'pointer', fontSize: '0.9rem', 
              color: isTelegramUsersActive ? 'white' : '#94a3b8',
              background: isTelegramUsersActive ? 'rgba(59, 130, 246, 0.1)' : 'transparent',
              transition: 'all 0.2s', position: 'relative'
            }}>
              {isTelegramUsersActive && <div className="active-line-left" />}
              <span style={{ color: isTelegramUsersActive ? '#60a5fa' : '#64748b', marginRight: '12px', display: 'flex' }}>
                <SendOutlined style={{ fontSize: '18px' }} />
              </span>
              Telegram Users
            </li>
          )}

          {/* USER MANAGEMENT */}
          {canSee('users') && (
            <li onClick={() => navigate('/users')} style={{
              marginBottom: '4px', display: 'flex', alignItems: 'center', padding: '12px 20px',
              cursor: 'pointer', fontSize: '0.9rem', color: isUsersActive ? 'white' : '#94a3b8',
              background: isUsersActive ? 'rgba(59, 130, 246, 0.1)' : 'transparent',
              transition: 'all 0.2s', position: 'relative'
            }}>
              {isUsersActive && <div className="active-line-left" />}
              <span style={{ color: isUsersActive ? '#60a5fa' : '#64748b', marginRight: '12px', display: 'flex' }}>
                <UserOutlined style={{ fontSize: '18px' }} />
              </span>
              User Management
            </li>
          )}

          {/* BOXES BUTTON */}
          <li onClick={() => setIsBoxesOpen(!isBoxesOpen)} style={{
            marginBottom: '4px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '12px 20px', cursor: 'pointer', fontSize: '0.9rem', 
            color: (isBoxesOpen || isInsideBox) ? 'white' : '#94a3b8',
            background: (isBoxesOpen || isInsideBox) ? 'rgba(59, 130, 246, 0.05)' : 'transparent',
            transition: 'all 0.2s', position: 'relative'
          }}>
            {(isBoxesOpen || isInsideBox) && <div className="active-line-left" />}
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <span style={{ color: (isBoxesOpen || isInsideBox) ? '#60a5fa' : '#64748b', marginRight: '12px', display: 'flex' }}>
                <Monitor size={18} />
              </span>
              Boxes
            </div>
            {isBoxesOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          </li>
        </ul>

        {isBoxesOpen && (
          <div style={{ padding: '5px 10px 15px 15px', background: 'rgba(15, 23, 42, 0.3)' }}>
            {loading ? <Spin size="small" style={{ marginLeft: '25px' }} /> : (
              <Tree
                showIcon blockNode onSelect={onSelect} onExpand={(keys) => setExpandedKeys(keys)}
                expandedKeys={expandedKeys} expandAction="click" treeData={treeData}
                style={{ background: 'transparent', color: '#94a3b8', fontSize: '0.85rem' }}
                className="sidebar-tree"
                selectedKeys={[currentBoxId]}
              />
            )}
          </div>
        )}
      </nav>

      <div style={{ padding: '20px', borderTop: '1px solid #334155', fontSize: '0.7rem', color: '#64748b' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '5px', marginBottom: '5px' }}>
          <div style={{ width: '6px', height: '6px', background: '#34d399', borderRadius: '50%' }}></div>
          System Operational
        </div>
        <span>Build v1.0.4-stable</span>
      </div>

      <style>{`
        .active-line-left {
          position: absolute; left: 0; top: 0; bottom: 0; width: 3px;
          background: #60a5fa; box-shadow: 0 0 10px #60a5fa, 0 0 20px #60a5fa; z-index: 10;
        }
        .sidebar-tree .ant-tree-node-content-wrapper { color: #94a3b8 !important; padding: 4px 4px !important; transition: 0.2s; position: relative; }
        .sidebar-tree .ant-tree-node-content-wrapper:hover { background: #334155 !important; color: white !important; }
        .sidebar-tree .ant-tree-node-selected { background: rgba(59, 130, 246, 0.2) !important; color: white !important; }
        .sidebar-tree .ant-tree-node-selected::before {
          content: ""; position: absolute; left: -15px; top: 0; bottom: 0; width: 3px;
          background: #60a5fa; box-shadow: 0 0 10px #60a5fa;
        }
        .sidebar-tree .ant-tree-switcher { color: #64748b !important; align-self: center; }
        .sidebar-tree .ant-tree-indent-unit { width: 12px !important; }
        .ant-tree { background: transparent !important; }
      `}</style>
    </div>
  );
};

export default Sidebar;